import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Eye, MousePointer, Globe, TrendingUp, Users, MapPin, Monitor, Smartphone, ExternalLink } from "lucide-react";
import { API_URL as API } from "../../config";

export default function AnalyticsPage() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const a = t.admin;

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/api/admin/analytics?days=${days}`);
    if (res.ok) setData(await res.json());
  }, [authFetch, days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return (
    <div data-testid="analytics-page">
      <h2 className="text-2xl font-bold text-gray-900 font-['Kanit'] mb-6">{a.websiteAnalytics}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 bg-white rounded-2xl animate-pulse" />)}</div>
    </div>
  );

  const maxDaily = Math.max(...(data.daily.map(d => d.count) || [1]), 1);

  return (
    <div data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{a.websiteAnalytics}</h2>
          <p className="text-gray-500 text-sm">{a.trackVisitorActivity}</p>
        </div>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} data-testid="analytics-period"
          className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
          <option value="7">{a.last7}</option>
          <option value="30">{a.last30}</option>
          <option value="90">{a.last90}</option>
        </select>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="stat-pageviews">
          <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mb-3">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900 font-['Kanit']">{data.total_pageviews.toLocaleString()}</p>
          <p className="text-sm text-gray-500">{a.pageViews}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="stat-unique-visitors">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center mb-3">
            <Users className="w-5 h-5 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900 font-['Kanit']">{(data.unique_visitors || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-500">{a.uniqueVisitors || "Unique Visitors"}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="stat-clicks">
          <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center mb-3">
            <MousePointer className="w-5 h-5 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900 font-['Kanit']">{data.total_clicks.toLocaleString()}</p>
          <p className="text-sm text-gray-500">{a.clicks}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="stat-countries">
          <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center mb-3">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900 font-['Kanit']">{data.countries.length}</p>
          <p className="text-sm text-gray-500">{a.countries}</p>
        </div>
      </div>

      {/* Daily Views Chart + Countries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> {a.dailyViews}
          </h3>
          {data.daily.length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center">{a.noDataYet}</p>
          ) : (
            <div className="flex items-end gap-1 h-40">
              {data.daily.slice(-30).map(d => (
                <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                  <div className="w-full bg-[#87CEEB] rounded-t transition-all hover:bg-[#6bb8d9]"
                    style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: "4px" }} />
                  <div className="absolute -top-8 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                    {d.date}: {d.count} {(a.views || "views").toLowerCase()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4" /> {a.topCountries}
          </h3>
          {data.countries.length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center">{a.noDataYet}</p>
          ) : (
            <div className="space-y-3">
              {data.countries.slice(0, 8).map(c => {
                const pct = data.total_pageviews > 0 ? (c.count / data.total_pageviews) * 100 : 0;
                return (
                  <div key={c.country}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{c.country}</span>
                      <span className="text-gray-500">{c.count} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#87CEEB] rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Cities + Devices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-cities">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> {a.topCities || "Top Cities"}
          </h3>
          {(!data.cities || data.cities.length === 0) ? (
            <p className="text-gray-400 text-sm py-8 text-center">{a.noDataYet}</p>
          ) : (
            <div className="space-y-2.5">
              {data.cities.slice(0, 10).map((c, i) => (
                <div key={`${c.city}-${c.country}-${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#87CEEB]/10 text-[#87CEEB] rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                    <span className="font-medium text-gray-700">{c.city}</span>
                    <span className="text-gray-400 text-xs">{c.country}</span>
                  </div>
                  <span className="text-gray-600 font-medium">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-devices">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Monitor className="w-4 h-4" /> {a.deviceBreakdown || "Device Breakdown"}
          </h3>
          {(!data.devices || data.devices.length === 0) ? (
            <p className="text-gray-400 text-sm py-8 text-center">{a.noDataYet}</p>
          ) : (
            <div className="space-y-4">
              {data.devices.map(d => {
                const pct = data.total_pageviews > 0 ? (d.count / data.total_pageviews) * 100 : 0;
                const isDesktop = d.device === "Desktop";
                return (
                  <div key={d.device} className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDesktop ? 'bg-indigo-100' : 'bg-emerald-100'}`}>
                      {isDesktop ? <Monitor className="w-5 h-5 text-indigo-600" /> : <Smartphone className="w-5 h-5 text-emerald-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{d.device}</span>
                        <span className="text-gray-500">{d.count} ({pct.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${isDesktop ? 'bg-indigo-400' : 'bg-emerald-400'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Referrers + Top Pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-referrers">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> {a.topReferrers || "Top Referrers"}
          </h3>
          {(!data.referrers || data.referrers.length === 0) ? (
            <p className="text-gray-400 text-sm py-8 text-center">{a.noReferrerData || "No referrer data yet"}</p>
          ) : (
            <div className="space-y-2.5">
              {data.referrers.map((r, i) => {
                let label = r.referrer;
                try { label = new URL(r.referrer).hostname; } catch {}
                return (
                  <div key={`${r.referrer}-${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-gray-700 truncate max-w-[70%]" title={r.referrer}>{label}</span>
                    <span className="text-gray-600 font-medium">{r.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-top-pages">
          <h3 className="font-semibold text-gray-700 mb-4">{a.topPages}</h3>
          {data.top_pages.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">{a.noDataYet}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">{a.page}</th>
                  <th className="pb-2 text-right">{a.views || "Views"}</th>
                </tr>
              </thead>
              <tbody>
                {data.top_pages.map(p => (
                  <tr key={p.page} className="border-b border-gray-50">
                    <td className="py-2.5 font-medium">{p.page}</td>
                    <td className="py-2.5 text-right text-gray-600">{p.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-700 mb-4">{a.recentActivity}</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {data.recent.slice(0, 20).map(r => (
            <div key={r.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50">
              <div className="flex items-center gap-2">
                {r.event_type === "pageview" ? <Eye className="w-3.5 h-3.5 text-blue-500" /> : <MousePointer className="w-3.5 h-3.5 text-emerald-500" />}
                <span className="text-gray-700">{r.page}</span>
                {r.element && <span className="text-gray-400 text-xs">({r.element})</span>}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                {r.city && r.city !== "Unknown" && <span>{r.city}</span>}
                <span>{r.country}</span>
                <span>{r.timestamp?.substring(0, 16).replace("T", " ")}</span>
              </div>
            </div>
          ))}
          {data.recent.length === 0 && <p className="text-gray-400 text-sm py-4 text-center">{a.noRecentActivity}</p>}
        </div>
      </div>
    </div>
  );
}
